/**
 * AdminAnalytics — Tracking V2 Phase 2 (consolidated).
 *
 * Features:
 *  - Period-KPIs (Heute / Gestern / 7T / 30T) für Visitors, Leads, Conversion-Rate
 *  - Filterbar (Zeitraum, Quelle, Kampagne, Keyword, Landingpage, Status, Event)
 *  - KPI-Grid, Time-Series-Chart, Funnel, Source-Pie, Source-Perf-Bar, Heatmap, Top-Tabellen
 *  - Google-Ads-Platzhalter-Karten (KPIs aus Phase-3 Integrations-Adapter)
 *  - Widget-System: ein-/ausblenden + Drag & Drop, persistiert pro User
 *  - Excel- & CSV-Export (server-side openpyxl)
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import AdminLayout from '@/components/AdminLayout';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  RefreshCw, Download, FileSpreadsheet, Users, MousePointerClick, MessageCircle,
  Phone, FileText, Eye, CheckCircle, TrendingUp, Euro, RotateCcw, Loader2,
  AlertCircle, Trophy, Calendar, Filter as FilterIcon, Sliders, GripVertical,
  EyeOff, BadgeAlert,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'sonner';

const PIE_COLORS = ['#4A331D', '#D4AA6A', '#8B5A2B', '#C19A6B', '#A0522D', '#6F4E37', '#704214'];
const DAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const fmtEUR = (n) => Number(n || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
const fmtNum = (n) => Number(n || 0).toLocaleString('de-DE');
const fmtPct = (n) => `${Number(n || 0).toFixed(2)}%`;
const fmtSec = (s) => {
  if (!s) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${(s / 3600).toFixed(1)} h`;
  return `${Math.round(s / 86400)} Tage`;
};

const downloadBlob = async (url, filename, params) => {
  try {
    const res = await api.get(url, { params, responseType: 'blob' });
    const blob = new Blob([res.data]);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(link.href);
    toast.success(`${filename} heruntergeladen`);
  } catch (e) {
    toast.error(`Download fehlgeschlagen: ${e?.response?.status || e.message}`);
  }
};

/* ---------- Small UI atoms ---------- */
const KpiCard = ({ icon: Icon, label, value, hint, color = 'bg-[#4A331D]/10 text-[#4A331D]', testid }) => (
  <div className="bg-white border rounded-xl p-4 flex flex-col gap-1" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-wider text-gray-500">{label}</span>
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4" />
      </span>
    </div>
    <div className="text-2xl font-bold text-[#4A331D] leading-tight">{value}</div>
    {hint && <div className="text-[11px] text-gray-400">{hint}</div>}
  </div>
);

const PeriodCell = ({ label, visitors, leads, cr, testid }) => (
  <div className="bg-white border rounded-xl p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">{label}</div>
    <div className="grid grid-cols-3 gap-2">
      <div>
        <div className="text-[10px] text-gray-400">Besucher</div>
        <div className="text-lg font-bold text-[#4A331D]">{fmtNum(visitors)}</div>
      </div>
      <div>
        <div className="text-[10px] text-gray-400">Leads</div>
        <div className="text-lg font-bold text-[#4A331D]">{fmtNum(leads)}</div>
      </div>
      <div>
        <div className="text-[10px] text-gray-400">CR</div>
        <div className="text-lg font-bold text-[#4A331D]">{fmtPct(cr)}</div>
      </div>
    </div>
  </div>
);

const Heatmap = ({ matrix }) => {
  const max = useMemo(() => {
    let m = 0;
    for (const row of matrix || []) for (const v of row) if (v > m) m = v;
    return m;
  }, [matrix]);
  if (!matrix || matrix.length === 0) return <p className="text-xs text-gray-400">Keine Heatmap-Daten.</p>;
  return (
    <div className="overflow-x-auto" data-testid="analytics-heatmap">
      <table className="text-[10px] border-collapse">
        <thead>
          <tr>
            <th className="w-8"></th>
            {Array.from({ length: 24 }, (_, h) => (
              <th key={h} className="w-7 text-center text-gray-500 font-normal">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row, di) => (
            <tr key={di}>
              <td className="text-right pr-2 text-gray-600 font-medium">{DAY_LABELS[di]}</td>
              {row.map((v, hi) => {
                const intensity = max > 0 ? v / max : 0;
                const bg = v === 0 ? 'rgba(74,51,29,0.04)' : `rgba(74,51,29,${0.15 + intensity * 0.8})`;
                const color = intensity > 0.55 ? '#fff' : '#4A331D';
                return (
                  <td key={hi} title={`${DAY_LABELS[di]} ${hi}:00 · ${v} Events`}
                      className="w-7 h-6 text-center align-middle rounded-sm"
                      style={{ backgroundColor: bg, color }}>
                    {v > 0 ? v : ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Funnel = ({ steps }) => {
  if (!steps || steps.length === 0) return null;
  const max = Math.max(...steps.map(s => s.count), 1);
  return (
    <div className="space-y-2" data-testid="analytics-funnel">
      {steps.map((s, i) => {
        const w = Math.max((s.count / max) * 100, 6);
        return (
          <div key={s.stage} className="flex items-center gap-3">
            <div className="w-44 text-xs text-gray-600">{s.stage}</div>
            <div className="flex-1 bg-gray-100 rounded-md h-7 relative overflow-hidden">
              <div className="h-full rounded-md transition-all"
                   style={{ width: `${w}%`, background: `linear-gradient(90deg, #4A331D, ${PIE_COLORS[i % PIE_COLORS.length]})` }} />
              <div className="absolute inset-0 flex items-center justify-between px-3 text-xs font-medium text-white mix-blend-difference">
                <span>{fmtNum(s.count)}</span>
                <span>{s.pct}%</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TopTable = ({ title, data, cols, testid }) => (
  <div className="bg-white border rounded-xl p-4 h-full" data-testid={testid}>
    <h3 className="text-sm font-semibold text-[#4A331D] mb-3">{title}</h3>
    {(!data || data.length === 0) ? (
      <p className="text-xs text-gray-400">Keine Daten.</p>
    ) : (
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-500 border-b">
            {cols.map(c => (
              <th key={c.k} className={`py-1.5 ${c.align === 'right' ? 'text-right' : 'text-left'} font-medium`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.slice(0, 10).map((row, i) => (
            <tr key={`${row[cols[0].k]}-${i}`} className="border-b last:border-0">
              {cols.map(c => (
                <td key={c.k} className={`py-1 ${c.align === 'right' ? 'text-right font-mono' : ''} text-gray-700 truncate max-w-[260px]`}>
                  {c.fmt ? c.fmt(row[c.k]) : (row[c.k] || '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

/* ---------- Google Ads (Phase 3 — live API) ---------- */
const AdsKpiRow = ({ ads, health, onSync, syncing }) => {
  const connected = !!ads?.connected;
  const cells = [
    { l: 'Kosten',         v: ads?.cost,            fmt: fmtEUR },
    { l: 'CPC',            v: ads?.cpc_eur,         fmt: fmtEUR },
    { l: 'CTR',            v: ads?.ctr_pct,         fmt: fmtPct },
    { l: 'Impressionen',   v: ads?.impressions,     fmt: fmtNum },
    { l: 'Klicks',         v: ads?.clicks,          fmt: fmtNum },
    { l: 'Kosten / Lead',  v: ads?.cost_per_lead,   fmt: fmtEUR },
    { l: 'Conv.-Wert',     v: ads?.conversion_value,fmt: fmtEUR },
    { l: 'ROAS',           v: ads?.roas,            fmt: (n) => n == null ? '—' : `${Number(n).toFixed(2)}×` },
  ];
  const lastSync = health?.last_sync_at;
  const statusLabel = health?.status === 'connected' ? 'verbunden'
                    : health?.status === 'error'     ? `Fehler · ${health?.error_code || ''}`
                    : 'nicht verbunden';
  const statusColor = health?.status === 'connected' ? 'bg-green-100 text-green-800'
                    : health?.status === 'error'     ? 'bg-red-100 text-red-800'
                    : 'bg-amber-100 text-amber-800';
  return (
    <div className="bg-white border rounded-xl p-4" data-testid="ads-kpis">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-[#4A331D] flex items-center gap-2">
          Google Ads
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide ${statusColor}`}>
            {connected ? <CheckCircle className="w-3 h-3" /> : <BadgeAlert className="w-3 h-3" />}
            {statusLabel}
          </span>
        </h3>
        <div className="flex items-center gap-2">
          {lastSync && <span className="text-[10px] text-gray-400">Letzter Sync: {new Date(lastSync).toLocaleString('de-DE')}</span>}
          <Button size="sm" variant="outline" onClick={onSync} disabled={syncing || !connected} data-testid="ads-sync-btn">
            <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Jetzt synchronisieren
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {cells.map(c => (
          <div key={c.l} className="border rounded-lg p-2 bg-gray-50/50">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{c.l}</div>
            <div className="text-base font-bold text-[#4A331D] mt-0.5">
              {c.v == null ? <span className="text-gray-300">—</span> : c.fmt(c.v)}
            </div>
          </div>
        ))}
      </div>
      {!connected && (
        <div className="mt-2 text-[11px] text-amber-700">
          {health?.message || 'Google Ads ist nicht verbunden.'}{' '}
          {health?.missing_env?.length > 0 && <span className="text-gray-500">Fehlend: {health.missing_env.join(', ')}</span>}
        </div>
      )}
    </div>
  );
};

/* ---------- Sortable wrapper ---------- */
const SortableWidget = ({ id, children, editing }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className={`relative ${editing ? 'ring-1 ring-amber-300 rounded-xl' : ''}`}>
      {editing && (
        <div className="absolute top-1 right-1 z-10 flex gap-1">
          <button {...attributes} {...listeners}
                  className="bg-white border rounded p-1 shadow hover:bg-amber-50 cursor-grab"
                  data-testid={`widget-drag-${id}`}
                  title="Verschieben">
            <GripVertical className="w-3.5 h-3.5 text-amber-700" />
          </button>
        </div>
      )}
      {children}
    </div>
  );
};

/* ---------- Default widget order ---------- */
const DEFAULT_WIDGETS = [
  { id: 'period_kpis',  visible: true },
  { id: 'kpi_grid',     visible: true },
  { id: 'ads_kpis',     visible: true },
  { id: 'ads_tables',   visible: true },
  { id: 'timeseries',   visible: true },
  { id: 'funnel_pie',   visible: true },
  { id: 'source_perf',  visible: true },
  { id: 'heatmap',      visible: true },
  { id: 'tops',         visible: true },
];

const WIDGET_LABELS = {
  period_kpis: 'Schnellübersicht (Heute / Gestern / 7T / 30T)',
  kpi_grid:    'KPI-Karten (Zeitraum)',
  ads_kpis:    'Google Ads KPIs',
  ads_tables:  'Google Ads Detail (Top-Kampagnen / Keywords / Suchbegriffe)',
  timeseries:  'Leads & Events pro Tag',
  funnel_pie:  'Funnel + Leads nach Quelle',
  source_perf: 'Performance je Quelle',
  heatmap:     'Heatmap (Tag × Stunde)',
  tops:        'Top-Tabellen (Kampagnen / Keywords / Landings / Quellen)',
};

const mergeLayout = (saved) => {
  if (!Array.isArray(saved) || saved.length === 0) return DEFAULT_WIDGETS;
  const known = new Set(DEFAULT_WIDGETS.map(w => w.id));
  // keep saved order, but ensure all default widgets are present (append missing)
  const result = saved.filter(w => known.has(w.id));
  const haveIds = new Set(result.map(w => w.id));
  for (const dw of DEFAULT_WIDGETS) {
    if (!haveIds.has(dw.id)) result.push(dw);
  }
  return result;
};

/* ===================== Main page ===================== */
const AdminAnalytics = () => {
  const [filters, setFilters] = useState({
    date_from: daysAgoISO(30),
    date_to: todayISO(),
    source: '', campaign: '', keyword: '', landing: '', status: '', event: '',
  });
  const [data, setData] = useState(null);
  const [ads, setAds] = useState(null);
  const [adsHealth, setAdsHealth] = useState(null);
  const [adsCampaigns, setAdsCampaigns] = useState([]);
  const [adsKeywords, setAdsKeywords] = useState([]);
  const [adsSearchTerms, setAdsSearchTerms] = useState([]);
  const [syncingAds, setSyncingAds] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);
  const [editing, setEditing] = useState(false);
  const layoutLoadedRef = useRef(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const queryParams = useMemo(() => {
    const out = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) out[k] = v; });
    return out;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashRes, kpisRes, healthRes, campRes, kwRes, stRes] = await Promise.all([
        api.get('/admin/analytics/dashboard', { params: queryParams }),
        api.get('/admin/integrations/google-ads/kpis', { params: queryParams }).catch(() => ({ data: { connected: false } })),
        api.get('/admin/integrations/google-ads/health').catch(() => ({ data: { connected: false, status: 'error' } })),
        api.get('/admin/integrations/google-ads/campaigns', { params: queryParams }).catch(() => ({ data: { items: [] } })),
        api.get('/admin/integrations/google-ads/keywords', { params: queryParams }).catch(() => ({ data: { items: [] } })),
        api.get('/admin/integrations/google-ads/search-terms', { params: queryParams }).catch(() => ({ data: { items: [] } })),
      ]);
      setData(dashRes.data);
      setAds(kpisRes.data);
      setAdsHealth(healthRes.data);
      setAdsCampaigns(campRes.data?.items || []);
      setAdsKeywords(kwRes.data?.items || []);
      setAdsSearchTerms(stRes.data?.items || []);
    } catch (e) {
      console.error('[Analytics] load failed', e);
      setError({ status: e.response?.status, detail: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  const syncGoogleAds = useCallback(async () => {
    setSyncingAds(true);
    try {
      await api.post('/admin/integrations/google-ads/sync', null, { params: queryParams });
      toast.success('Google Ads synchronisiert');
      await load();
    } catch (e) {
      toast.error('Sync fehlgeschlagen: ' + (e?.response?.data?.detail?.message || e?.response?.status || e.message));
    } finally {
      setSyncingAds(false);
    }
  }, [queryParams, load]);

  useEffect(() => { load(); }, [load]);

  // Load widget layout from backend (once)
  useEffect(() => {
    if (layoutLoadedRef.current) return;
    layoutLoadedRef.current = true;
    api.get('/admin/analytics/dashboard-layout')
      .then(res => { setWidgets(mergeLayout(res.data?.widgets)); })
      .catch(() => { /* keep defaults */ });
  }, []);

  const saveLayout = useCallback(async (next) => {
    try {
      await api.put('/admin/analytics/dashboard-layout', { widgets: next });
    } catch (e) {
      toast.error('Layout konnte nicht gespeichert werden');
    }
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setWidgets(curr => {
      const oldIdx = curr.findIndex(w => w.id === active.id);
      const newIdx = curr.findIndex(w => w.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return curr;
      const next = arrayMove(curr, oldIdx, newIdx);
      saveLayout(next);
      return next;
    });
  };

  const toggleWidget = (id) => {
    setWidgets(curr => {
      const next = curr.map(w => w.id === id ? { ...w, visible: !w.visible } : w);
      saveLayout(next);
      return next;
    });
  };

  const resetLayout = async () => {
    setWidgets(DEFAULT_WIDGETS);
    await saveLayout(DEFAULT_WIDGETS);
    toast.success('Layout zurückgesetzt');
  };

  const setRange = (days) => setFilters(f => ({ ...f, date_from: daysAgoISO(days), date_to: todayISO() }));
  const resetFilters = () => setFilters({
    date_from: daysAgoISO(30), date_to: todayISO(),
    source: '', campaign: '', keyword: '', landing: '', status: '', event: '',
  });

  const kpis = data?.kpis;
  const pk = data?.period_kpis;

  const sourcePieData = useMemo(() => {
    if (!data?.leads_by_source) return [];
    return data.leads_by_source.filter(r => (r.leads || 0) > 0).map(r => ({ name: r.source || '—', value: r.leads }));
  }, [data]);

  const sourcePerformance = useMemo(() => {
    if (!data) return [];
    return (data.leads_by_source || []).slice(0, 8).map(r => ({
      source: r.source || '—', leads: r.leads || 0, revenue: r.revenue || 0,
    }));
  }, [data]);

  /* ---------- Widget renderers ---------- */
  const renderWidget = (id) => {
    if (!kpis) return null;
    switch (id) {
      case 'period_kpis':
        return pk ? (
          <div className="bg-white border rounded-xl p-4" data-testid="widget-period-kpis">
            <h3 className="text-sm font-semibold text-[#4A331D] mb-3">Schnellübersicht (Live, unabhängig vom Filter)</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <PeriodCell label="Heute"           visitors={pk.today.visitors}     leads={pk.today.leads}     cr={pk.today.conversion_rate_pct}     testid="period-today" />
              <PeriodCell label="Gestern"         visitors={pk.yesterday.visitors} leads={pk.yesterday.leads} cr={pk.yesterday.conversion_rate_pct} testid="period-yesterday" />
              <PeriodCell label="Letzte 7 Tage"   visitors={pk.last_7d.visitors}   leads={pk.last_7d.leads}   cr={pk.last_7d.conversion_rate_pct}   testid="period-7d" />
              <PeriodCell label="Letzte 30 Tage"  visitors={pk.last_30d.visitors}  leads={pk.last_30d.leads}  cr={pk.last_30d.conversion_rate_pct}  testid="period-30d" />
            </div>
          </div>
        ) : null;
      case 'kpi_grid':
        return (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="analytics-kpis">
            <KpiCard icon={Users} label="Besucher" value={fmtNum(kpis.visitors)} hint={`${fmtNum(kpis.sessions)} Sessions`} color="bg-blue-50 text-blue-700" testid="kpi-visitors" />
            <KpiCard icon={MousePointerClick} label="Page Views" value={fmtNum(kpis.page_views)} color="bg-indigo-50 text-indigo-700" testid="kpi-pageviews" />
            <KpiCard icon={MessageCircle} label="WhatsApp-Klicks" value={fmtNum(kpis.whatsapp_clicks)} color="bg-green-50 text-green-700" testid="kpi-whatsapp" />
            <KpiCard icon={Phone} label="Anrufe" value={fmtNum(kpis.call_clicks)} color="bg-sky-50 text-sky-700" testid="kpi-calls" />
            <KpiCard icon={FileText} label="Formular geöffnet" value={fmtNum(kpis.form_opens)} hint={`${fmtNum(kpis.form_submits)} gesendet`} color="bg-amber-50 text-amber-700" testid="kpi-formopens" />
            <KpiCard icon={Eye} label="Danke-Seite" value={fmtNum(kpis.lead_thank_you_views)} color="bg-purple-50 text-purple-700" testid="kpi-thankyou" />
            <KpiCard icon={CheckCircle} label="Leads" value={fmtNum(kpis.leads)} hint={`${fmtNum(kpis.returning_leads)} wiederkehrend`} color="bg-emerald-50 text-emerald-700" testid="kpi-leads" />
            <KpiCard icon={Trophy} label="Gewonnen" value={fmtNum(kpis.won_leads)} hint={`${kpis.win_rate_pct}% Win-Rate`} color="bg-yellow-50 text-yellow-800" testid="kpi-won" />
            <KpiCard icon={TrendingUp} label="Conversion-Rate" value={`${kpis.conversion_rate_pct}%`} hint="Lead / Besucher" color="bg-rose-50 text-rose-700" testid="kpi-cr" />
            <KpiCard icon={Calendar} label="Ø Zeit → Conversion" value={fmtSec(kpis.avg_time_to_conversion_sec)} color="bg-slate-100 text-slate-700" testid="kpi-ttc" />
            <KpiCard icon={Euro} label="Umsatz" value={fmtEUR(kpis.revenue_eur)} color="bg-[#D4AA6A]/30 text-[#4A331D]" testid="kpi-revenue" />
            <KpiCard icon={Euro} label="Gewinn" value={fmtEUR(kpis.profit_eur)} color="bg-[#4A331D]/15 text-[#4A331D]" testid="kpi-profit" />
          </div>
        );
      case 'ads_kpis':
        return <AdsKpiRow ads={ads} health={adsHealth} onSync={syncGoogleAds} syncing={syncingAds} />;
      case 'ads_tables': {
        const connected = !!adsHealth?.connected;
        if (!connected) return null;
        // Sort & slice helpers
        const sortByCost = (rows, n=10) => [...rows].sort((a,b)=>(b.cost_eur||0)-(a.cost_eur||0)).slice(0,n);
        const wastedSearchTerms = adsSearchTerms.filter(t => (t.cost_eur||0)>0 && (t.conversions||0)===0)
                                                .sort((a,b)=>(b.cost_eur||0)-(a.cost_eur||0)).slice(0,10);
        const worstKeywords = [...adsKeywords]
          .filter(k => (k.clicks||0)>=5)
          .sort((a,b)=>{
            const aCpl = (a.conversions||0)>0 ? a.cost_eur/a.conversions : 99999;
            const bCpl = (b.conversions||0)>0 ? b.cost_eur/b.conversions : 99999;
            return bCpl - aCpl;
          }).slice(0,10);
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="ads-tables">
            <TopTable title="Top Kampagnen (Google Ads)" data={sortByCost(adsCampaigns)} cols={[
              { k:'name', label:'Kampagne' },
              { k:'cost_eur', label:'Kosten', align:'right', fmt: fmtEUR },
              { k:'clicks', label:'Klicks', align:'right', fmt: fmtNum },
              { k:'conversions', label:'Conv.', align:'right', fmt: (n)=>Number(n||0).toFixed(1) },
              { k:'roas', label:'ROAS', align:'right', fmt: (n)=>n==null?'—':`${Number(n).toFixed(2)}×` },
            ]} testid="ads-top-campaigns" />
            <TopTable title="Top Keywords (Google Ads)" data={sortByCost(adsKeywords)} cols={[
              { k:'keyword', label:'Keyword' },
              { k:'cost_eur', label:'Kosten', align:'right', fmt: fmtEUR },
              { k:'clicks', label:'Klicks', align:'right', fmt: fmtNum },
              { k:'conversions', label:'Conv.', align:'right', fmt: (n)=>Number(n||0).toFixed(1) },
            ]} testid="ads-top-keywords" />
            <TopTable title="Schlechteste Keywords (höchste CPA / ohne Conv.)" data={worstKeywords} cols={[
              { k:'keyword', label:'Keyword' },
              { k:'cost_eur', label:'Kosten', align:'right', fmt: fmtEUR },
              { k:'clicks', label:'Klicks', align:'right', fmt: fmtNum },
              { k:'cost_per_conversion_eur', label:'CPA', align:'right', fmt: (n)=>n==null?'—':fmtEUR(n) },
            ]} testid="ads-worst-keywords" />
            <TopTable title="Suchbegriffe — Kosten ohne Conversion" data={wastedSearchTerms} cols={[
              { k:'search_term', label:'Suchbegriff' },
              { k:'cost_eur', label:'Kosten', align:'right', fmt: fmtEUR },
              { k:'clicks', label:'Klicks', align:'right', fmt: fmtNum },
              { k:'campaign', label:'Kampagne' },
            ]} testid="ads-wasted-search-terms" />
          </div>
        );
      }
      case 'timeseries':
        return (
          <div className="bg-white border rounded-xl p-4" data-testid="chart-timeseries">
            <h3 className="text-sm font-semibold text-[#4A331D] mb-3">Leads &amp; Events pro Tag</h3>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={data.time_series} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={20} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="leads"  stroke="#4A331D" strokeWidth={2} dot={false} name="Leads" />
                  <Line type="monotone" dataKey="events" stroke="#D4AA6A" strokeWidth={2} dot={false} name="Tracking-Events" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      case 'funnel_pie':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-[#4A331D] mb-3">Funnel-Visualisierung</h3>
              <Funnel steps={data.funnel} />
            </div>
            <div className="bg-white border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-[#4A331D] mb-3">Leads nach Quelle</h3>
              {sourcePieData.length === 0 ? (
                <p className="text-xs text-gray-400">Keine klassifizierten Leads im Zeitraum.</p>
              ) : (
                <div style={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={sourcePieData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={90}
                           label={(e) => `${e.name}: ${e.value}`}>
                        {sourcePieData.map((entry, idx) => (
                          <Cell key={entry.name} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        );
      case 'source_perf':
        return sourcePerformance.length > 0 ? (
          <div className="bg-white border rounded-xl p-4" data-testid="chart-source-perf">
            <h3 className="text-sm font-semibold text-[#4A331D] mb-3">Performance je Quelle</h3>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={sourcePerformance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                  <XAxis dataKey="source" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left"  tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left"  dataKey="leads"   fill="#4A331D" name="Leads" />
                  <Bar yAxisId="right" dataKey="revenue" fill="#D4AA6A" name="Umsatz (€)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : null;
      case 'heatmap':
        return (
          <div className="bg-white border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-[#4A331D] mb-3">Aktivität nach Tag &amp; Stunde</h3>
            <Heatmap matrix={data.heatmap} />
          </div>
        );
      case 'tops':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <TopTable title="Top Kampagnen (nach Leads)" data={data.leads_by_campaign} cols={[
              { k: 'campaign', label: 'Kampagne' },
              { k: 'leads',    label: 'Leads', align: 'right' },
              { k: 'revenue',  label: 'Umsatz', align: 'right', fmt: fmtEUR },
            ]} testid="top-campaigns" />
            <TopTable title="Top Keywords (Events)" data={data.top_keywords} cols={[
              { k: 'keyword', label: 'Keyword' }, { k: 'events', label: 'Events', align: 'right' },
            ]} testid="top-keywords" />
            <TopTable title="Top Landingpages" data={data.top_landing_pages} cols={[
              { k: 'landing_page', label: 'Landingpage' }, { k: 'events', label: 'Events', align: 'right' },
            ]} testid="top-landing" />
            <TopTable title="Top Quellen (Events)" data={data.top_sources} cols={[
              { k: 'source', label: 'Quelle' }, { k: 'events', label: 'Events', align: 'right' },
            ]} testid="top-sources" />
          </div>
        );
      default: return null;
    }
  };

  return (
    <AdminLayout title="Analytics & Reports">
      {/* Filter bar */}
      <div className="bg-white border rounded-xl p-4 mb-5" data-testid="analytics-filters">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#4A331D]">
            <FilterIcon className="w-4 h-4" /> Filter
          </div>
          <div className="flex gap-2 flex-wrap">
            {[{l:'7T',d:7},{l:'30T',d:30},{l:'90T',d:90},{l:'365T',d:365}].map(p => (
              <button key={p.l} onClick={() => setRange(p.d)}
                      className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                      data-testid={`range-${p.d}`}>{p.l}</button>
            ))}
            <button onClick={resetFilters} className="text-xs px-2 py-1 border rounded hover:bg-gray-50 inline-flex items-center gap-1" data-testid="filters-reset">
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <div><Label className="text-xs flex items-center gap-1"><Calendar className="w-3 h-3" /> Von</Label>
            <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="filter-from" /></div>
          <div><Label className="text-xs flex items-center gap-1"><Calendar className="w-3 h-3" /> Bis</Label>
            <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="filter-to" /></div>
          <div><Label className="text-xs">Quelle</Label>
            <Input value={filters.source} onChange={e => setFilters(f => ({ ...f, source: e.target.value }))} placeholder="Google Ads, Organisch, …" data-testid="filter-source" /></div>
          <div><Label className="text-xs">Kampagne</Label>
            <Input value={filters.campaign} onChange={e => setFilters(f => ({ ...f, campaign: e.target.value }))} placeholder="utm_campaign" data-testid="filter-campaign" /></div>
          <div><Label className="text-xs">Keyword</Label>
            <Input value={filters.keyword} onChange={e => setFilters(f => ({ ...f, keyword: e.target.value }))} placeholder="utm_term" data-testid="filter-keyword" /></div>
          <div><Label className="text-xs">Landingpage</Label>
            <Input value={filters.landing} onChange={e => setFilters(f => ({ ...f, landing: e.target.value }))} placeholder="/projekt..." data-testid="filter-landing" /></div>
          <div><Label className="text-xs">Lead-Status</Label>
            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                    className="w-full h-10 border border-gray-200 rounded-md bg-white px-2 text-sm" data-testid="filter-status">
              <option value="">alle</option>
              <option value="Neu">Neu</option>
              <option value="Kontaktiert">Kontaktiert</option>
              <option value="Angebot gesendet">Angebot gesendet</option>
              <option value="Termin">Termin</option>
              <option value="Angebot">Angebot</option>
              <option value="Gewonnen">Gewonnen</option>
              <option value="Verloren">Verloren</option>
              <option value="Abgeschlossen">Abgeschlossen</option>
            </select></div>
          <div><Label className="text-xs">Event-Typ</Label>
            <select value={filters.event} onChange={e => setFilters(f => ({ ...f, event: e.target.value }))}
                    className="w-full h-10 border border-gray-200 rounded-md bg-white px-2 text-sm" data-testid="filter-event">
              <option value="">alle</option>
              <option value="page_view">page_view</option>
              <option value="whatsapp_click">whatsapp_click</option>
              <option value="call_click">call_click</option>
              <option value="form_open">form_open</option>
              <option value="form_submit">form_submit</option>
              <option value="lead_thank_you_view">lead_thank_you_view</option>
              <option value="scroll_depth">scroll_depth</option>
              <option value="page_exit">page_exit</option>
            </select></div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button onClick={load} disabled={loading} size="sm" className="bg-[#4A331D] hover:bg-[#593B23]" data-testid="apply-filters">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
          <Button onClick={() => downloadBlob('/admin/analytics/export.xlsx',
              `bekajbau_analytics_${filters.date_from}_${filters.date_to}.xlsx`, queryParams)}
                  size="sm" variant="outline" data-testid="export-xlsx-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2 text-green-700" /> Excel Export (.xlsx)
          </Button>
          <Button onClick={() => downloadBlob('/admin/analytics/export.csv',
              `bekajbau_leads_${filters.date_from}_${filters.date_to}.csv`, queryParams)}
                  size="sm" variant="outline" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2 text-blue-700" /> CSV Export
          </Button>
          <Button onClick={() => setEditing(e => !e)} size="sm"
                  variant={editing ? 'default' : 'outline'}
                  className={editing ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
                  data-testid="customize-toggle">
            <Sliders className="w-4 h-4 mr-2" /> {editing ? 'Fertig' : 'Dashboard anpassen'}
          </Button>
        </div>
      </div>

      {/* Customize panel (visible when editing) */}
      {editing && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5" data-testid="customize-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-amber-900">Dashboard anpassen</div>
            <Button size="sm" variant="outline" onClick={resetLayout} data-testid="customize-reset">
              <RotateCcw className="w-3 h-3 mr-1" /> Layout zurücksetzen
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {widgets.map(w => (
              <label key={w.id} className="flex items-center gap-2 text-xs bg-white border rounded px-2 py-1.5 cursor-pointer hover:bg-gray-50">
                <input type="checkbox" checked={w.visible} onChange={() => toggleWidget(w.id)}
                       data-testid={`toggle-${w.id}`} />
                <span className="text-gray-700">{WIDGET_LABELS[w.id] || w.id}</span>
                {!w.visible && <EyeOff className="w-3 h-3 ml-auto text-gray-300" />}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-amber-700 mt-2">Per Drag &amp; Drop am Greifer-Icon (rechts oben am Widget) verschieben. Änderungen werden automatisch gespeichert.</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex gap-2 items-start" data-testid="analytics-error">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
          <div className="text-sm text-red-800">
            <div className="font-semibold">Analytics konnte nicht geladen werden</div>
            <div className="text-xs">Status {error.status || '–'} · {error.detail}</div>
          </div>
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-gray-400" data-testid="analytics-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Lade Daten…
        </div>
      ) : kpis ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={widgets.filter(w => w.visible).map(w => w.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-5">
              {widgets.filter(w => w.visible).map(w => (
                <SortableWidget key={w.id} id={w.id} editing={editing}>
                  {renderWidget(w.id)}
                </SortableWidget>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : null}
    </AdminLayout>
  );
};

export default AdminAnalytics;
